import React, { useCallback, useEffect, useState } from 'react';
import {
  Shield, Lock, Copy, Check, AlertCircle, Unlock, QrCode, ChevronDown, Minus, Plus,
} from 'lucide-react';
import {
  SHARE_PAGE_WIDTH,
  CONTENT_WIDTH,
  EXPIRY_OPTIONS,
  expiryDisplayLabel,
  TEXT,
  SURFACE,
  SURFACE_RAISED,
  INSET,
  TEXTAREA,
  MONO_FIELD,
  SEGMENT_WRAP,
  segmentOptionClass,
  SWITCH_HIT,
  switchTrackClass,
  switchThumbClass,
  TAG_OUTLINE,
  TAG_ACCENT,
  inputClass,
} from '../../uiTokens';
import Button from '../ui/Button';
import QrCard from '../ui/QrCard';
import DictateButton from '../ui/DictateButton';
import DemoVideo from '../ui/DemoVideo';
import useOnDeviceDictation from '../../hooks/useOnDeviceDictation';
import { MAX_ALLOWED_IPS_LENGTH, MAX_EMAIL_LENGTH, MAX_SECRET_LENGTH } from '../../constants';
import { copyToClipboard } from '../../utils/clipboard';
import { formatRemainingLabel } from '../../utils/ttl';

function selectInputText(event) {
  event.target.select();
}

async function handleCopy(text, onSuccess, onError) {
  try {
    await copyToClipboard(text);
    onSuccess();
  } catch {
    onError('Could not copy to clipboard. Select the text and copy manually.');
  }
}

function Spinner({ className = 'h-4 w-4' }) {
  return (
    <span
      className={`${className} inline-block rounded-full border-2 animate-spin`}
      style={{ borderColor: 'color-mix(in srgb, currentColor 25%, transparent)', borderTopColor: 'currentColor' }}
      aria-hidden="true"
    />
  );
}

function ShareOptionToggle({ id, checked, onChange, label, hint, children }) {
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-start justify-between gap-3">
        <label htmlFor={id} className="flex-1 min-w-0 cursor-pointer pt-2.5">
          <span className="block text-sm font-medium text-fg">{label}</span>
          {hint && <span className={`${TEXT.caption} block mt-0.5`}>{hint}</span>}
        </label>
        <button
          type="button"
          id={id}
          role="switch"
          aria-checked={checked}
          onClick={onChange}
          className={SWITCH_HIT}
        >
          <span className={switchTrackClass(checked)} aria-hidden="true">
            <span className={switchThumbClass(checked)} />
          </span>
        </button>
      </div>
      {checked && <div className="flex flex-col gap-1.5 animate-rise-fast">{children}</div>}
    </div>
  );
}

function StatusCard({ icon, tone = 'neutral', title, children, actions }) {
  const iconTone = {
    neutral: 'text-accent',
    success: 'text-success',
    danger: 'text-danger',
  }[tone];
  return (
    <div className={`${SURFACE_RAISED} p-6 sm:p-8 animate-rise`} role={tone === 'danger' ? 'alert' : undefined}>
      <div className="flex items-start gap-3.5">
        {icon && <span className={`mt-0.5 shrink-0 ${iconTone}`}>{icon}</span>}
        <div className="min-w-0 flex-1">
          <h2 className={TEXT.h2}>{title}</h2>
          {children}
        </div>
      </div>
      {actions && <div className="mt-6 flex flex-wrap items-center gap-3">{actions}</div>}
    </div>
  );
}

function SharedLinkView({
  unwrappedData, error, isDecrypting, secretCopied,
  setSecretCopied, setError, onCreateNew, onReveal, canReveal, hasValidKey,
  onRetryUnwrap, canRetryUnwrap,
}) {
  return (
    <div className={`${SHARE_PAGE_WIDTH} pt-10 sm:pt-16 pb-16 sm:pb-24`}>
      {unwrappedData ? (
        <div className={`${SURFACE_RAISED} p-6 sm:p-8 animate-rise-slow`}>
          <div className="flex items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-2.5">
              <Unlock className="h-[18px] w-[18px] text-success" aria-hidden="true" />
              <h2 className={TEXT.h3}>Your secret</h2>
            </div>
            <span className={TAG_ACCENT}>Opened once</span>
          </div>
          <div className={`${INSET} p-4 max-h-[60vh] overflow-y-auto`}>
            <pre className="whitespace-pre-wrap font-mono text-[13px] leading-relaxed break-anywhere m-0 text-fg">
              {unwrappedData}
            </pre>
          </div>
          <Button
            block
            size="lg"
            className="mt-4"
            onClick={() => handleCopy(
              unwrappedData,
              () => {
                setSecretCopied(true);
                setTimeout(() => setSecretCopied(false), 2200);
              },
              (message) => {
                setError(message);
                setTimeout(() => setError(''), 5000);
              },
            )}
          >
            {secretCopied
              ? <><Check className="h-4 w-4" aria-hidden="true" /> Copied</>
              : <><Copy className="h-4 w-4" aria-hidden="true" /> Copy to clipboard</>}
          </Button>
          <div className="mt-6 pt-5 border-t border-line flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className={TEXT.small}>This link is now dead. It cannot be opened again.</p>
            <Button variant="ghost" size="sm" onClick={onCreateNew}>
              <Lock className="h-3.5 w-3.5" aria-hidden="true" />
              Share another secret
            </Button>
          </div>
        </div>
      ) : isDecrypting ? (
        <div className={`${SURFACE_RAISED} p-8 sm:p-10 text-center`} role="status" aria-live="polite" aria-busy="true">
          <Spinner className="h-6 w-6 text-accent" />
          <p className={`${TEXT.h3} mt-4`}>Opening secret</p>
          <p className={`${TEXT.small} mt-1.5`}>Decrypting in your browser. This usually takes a moment.</p>
        </div>
      ) : !hasValidKey ? (
        <StatusCard
          tone="danger"
          icon={<AlertCircle className="h-5 w-5" aria-hidden="true" />}
          title="This link looks incomplete"
        >
          <p className={`${TEXT.body} mt-2`}>
            Copy the entire URL you received, including the part after #.
          </p>
        </StatusCard>
      ) : error ? (
        <StatusCard
          tone="danger"
          icon={<AlertCircle className="h-5 w-5" aria-hidden="true" />}
          title="Could not open this link"
          actions={canRetryUnwrap && (
            <Button variant="secondary" onClick={onRetryUnwrap}>Try again</Button>
          )}
        >
          <p className="text-sm mt-2 text-danger break-words">{error}</p>
          <p className={`${TEXT.small} mt-2`}>Ask the sender for a new link.</p>
        </StatusCard>
      ) : (
        <div className={`${SURFACE_RAISED} p-6 sm:p-8 animate-rise`}>
          <span className={TAG_OUTLINE}>
            <Shield className="h-3 w-3 shrink-0" strokeWidth={2} aria-hidden="true" />
            One-time link
          </span>
          <h2 className={`${TEXT.h1} mt-4`}>Someone shared a secret with you</h2>
          <p className={`${TEXT.body} mt-3 max-w-[48ch]`}>
            This link works once. After you reveal it, it cannot be opened again - by you or anyone else.
          </p>
          <Button block size="lg" className="mt-7" onClick={onReveal} disabled={!canReveal}>
            <Unlock className="h-4 w-4" aria-hidden="true" />
            Reveal secret
          </Button>
          <p className={`${TEXT.caption} mt-3 text-center`}>Decrypted in your browser. Nothing is sent back to the sender.</p>
        </div>
      )}
    </div>
  );
}

export default function HomePage({
  isSharedLink,
  unwrappedData,
  error,
  isDecrypting,
  secretCopied,
  setSecretCopied,
  setError,
  showEncryptResult,
  secretData,
  setSecretData,
  handleEncrypt,
  isEncrypting,
  wrapTtl,
  setWrapTtl,
  enableNotification,
  setEnableNotification,
  notificationEmail,
  setNotificationEmail,
  emailValidation,
  validateEmail,
  setEmailValidation,
  numLinks,
  setNumLinks,
  enableIPRestriction,
  setEnableIPRestriction,
  customAllowedIPs,
  setCustomAllowedIPs,
  wrappedToken,
  wrappedTokens,
  showQRCodes,
  setShowQRCodes,
  linkCopied,
  setLinkCopied,
  linkCreatedAt,
  linkCreatedAtMs,
  generateSecretLink,
  resetEncryptForm,
  onCreateNew,
  onReveal,
  canReveal,
  hasValidKey,
  onRetryUnwrap,
  canRetryUnwrap,
  onNavigate,
}) {
  const [settingsRevealed, setSettingsRevealed] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [copiedLinks, setCopiedLinks] = useState(new Set());
  const [remainingLabel, setRemainingLabel] = useState('');

  const hasSecret = secretData.trim().length > 0;
  const expiryLabel = expiryDisplayLabel(wrapTtl);
  const shareLink = wrappedToken ? generateSecretLink(wrappedToken) : '';
  const hasMultiLinks = wrappedTokens.length > 0;

  const appendTranscript = useCallback((text) => {
    setSecretData((previous) => {
      const separator = previous && !/\s$/.test(previous) ? ' ' : '';
      return `${previous}${separator}${text}`.slice(0, MAX_SECRET_LENGTH);
    });
  }, [setSecretData]);

  const dictation = useOnDeviceDictation({ onTranscript: appendTranscript });
  const stopDictation = dictation.stop;

  useEffect(() => {
    if (hasSecret) {
      setSettingsRevealed(true);
    }
  }, [hasSecret]);

  // The create form is gone once the link exists; release the microphone.
  useEffect(() => {
    if (showEncryptResult) {
      stopDictation();
    }
  }, [showEncryptResult, stopDictation]);

  useEffect(() => {
    if (!showEncryptResult || !linkCreatedAtMs) {
      setRemainingLabel('');
      return undefined;
    }
    const tick = () => setRemainingLabel(formatRemainingLabel(linkCreatedAtMs, wrapTtl));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [showEncryptResult, linkCreatedAtMs, wrapTtl]);

  if (isSharedLink) {
    return (
      <SharedLinkView
        unwrappedData={unwrappedData}
        error={error}
        isDecrypting={isDecrypting}
        secretCopied={secretCopied}
        setSecretCopied={setSecretCopied}
        setError={setError}
        onCreateNew={onCreateNew}
        onReveal={onReveal}
        canReveal={canReveal}
        hasValidKey={hasValidKey}
        onRetryUnwrap={onRetryUnwrap}
        canRetryUnwrap={canRetryUnwrap}
      />
    );
  }

  const hint = hasSecret
    ? 'Encrypted here in your browser - the key never reaches our servers.'
    : 'Nothing is uploaded until you create the link.';

  const createLabel = hasSecret
    ? (numLinks === 1 ? 'Create one-time link' : `Create ${numLinks} one-time links`)
    : 'Paste a secret to continue';

  const nextUp = hasSecret
    ? (numLinks === 1
      ? `Next: a link you copy once. It self-destructs on first open, or after ${expiryLabel}.`
      : `Next: ${numLinks} links to the same secret. Each opens once, or expires after ${expiryLabel}.`)
    : 'You get a link that works once, then destroys itself.';

  const notifyRequired = enableNotification && !notificationEmail.trim();
  const notifyInvalid = enableNotification && notificationEmail.trim() && emailValidation.isValid === false;
  const cannotCreate = !hasSecret || isEncrypting || notifyRequired || notifyInvalid;

  const checklist = [
    'Opens once. The link dies the moment it is read.',
    `Expires after ${expiryLabel} even if nobody opens it.`,
  ];
  if (enableNotification) {
    checklist.push("You'll get an email the moment it's opened.");
  }
  if (enableIPRestriction) {
    checklist.push(
      customAllowedIPs.trim()
        ? `Only ${customAllowedIPs.trim()} can open the link.`
        : 'Only your current IP can open the link.',
    );
  }
  if (numLinks > 1) {
    checklist.push(`${numLinks} independent links - each works once.`);
  }

  const handleReset = () => {
    stopDictation();
    setSettingsRevealed(false);
    setShowAdvanced(false);
    setCopiedLinks(new Set());
    resetEncryptForm();
  };

  const copyLinkAtIndex = (fullUrl, index) => {
    handleCopy(
      fullUrl,
      () => {
        if (index === 'single') {
          setLinkCopied(true);
          setTimeout(() => setLinkCopied(false), 2200);
        } else {
          setCopiedLinks((prev) => new Set([...prev, index]));
          setTimeout(() => {
            setCopiedLinks((prev) => {
              const next = new Set(prev);
              next.delete(index);
              return next;
            });
          }, 2200);
        }
      },
      setError,
    );
  };

  const optionsCount = (enableNotification ? 1 : 0) + (enableIPRestriction ? 1 : 0) + (numLinks > 1 ? 1 : 0);

  return (
    <div className="relative">
      <div className="dot-grid pointer-events-none absolute inset-x-0 top-0 h-72 -z-10" aria-hidden="true" />

      <div className={`${SHARE_PAGE_WIDTH} pt-10 sm:pt-16 ${showEncryptResult ? 'pb-16 sm:pb-24' : 'pb-0'}`}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
          <h1 className={TEXT.display}>Share a secret once</h1>
          <span className={`${TAG_OUTLINE} shrink-0 self-start sm:self-auto`}>
            <Shield className="h-3 w-3 shrink-0" strokeWidth={2} aria-hidden="true" />
            Encrypted in your browser
          </span>
        </div>

        <p className={`${TEXT.lead} mt-3 mb-8 max-w-[48ch]`}>
          Paste it below. You get a link that works one time, then it is gone.
        </p>

        {!showEncryptResult ? (
          <div className="flex flex-col gap-4">
            <div className={`${SURFACE} p-4 sm:p-5 flex flex-col gap-2.5`}>
              <textarea
                id="secret-input"
                value={secretData}
                aria-label="Your secret"
                aria-describedby="secret-hint secret-counter"
                onChange={(e) => setSecretData(e.target.value.slice(0, MAX_SECRET_LENGTH))}
                placeholder="Password, API key, private note..."
                className={TEXTAREA}
              />
              <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 ${TEXT.caption}`}>
                <span id="secret-hint" className="mr-auto">{hint}</span>
                {hasSecret && (
                  <span id="secret-counter" className="tabular-nums">
                    {secretData.length.toLocaleString()} / {MAX_SECRET_LENGTH.toLocaleString()}
                  </span>
                )}
                <DictateButton dictation={dictation} disabled={isEncrypting} />
              </div>
            </div>

            {settingsRevealed && (
              <div className={`${SURFACE} p-4 sm:p-5 flex flex-col gap-5 animate-rise`}>
                <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-sm font-medium text-fg">The link stops working after</span>
                  <div className={SEGMENT_WRAP} role="radiogroup" aria-label="Link expiration">
                    {EXPIRY_OPTIONS.map((option, index) => (
                      <label
                        key={option.value}
                        className={`${segmentOptionClass(wrapTtl === option.value)} ${index > 0 ? 'border-l border-line' : ''}`}
                      >
                        <input
                          type="radio"
                          name="expiry"
                          value={option.value}
                          checked={wrapTtl === option.value}
                          onChange={() => setWrapTtl(option.value)}
                          className="sr-only"
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="border-t border-line" />

                <button
                  type="button"
                  onClick={() => setShowAdvanced((open) => !open)}
                  aria-expanded={showAdvanced}
                  className="inline-flex items-center gap-1.5 self-start -my-1 py-1 text-sm font-medium text-accent-text hover:text-accent-hover transition-colors rounded-md"
                >
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${showAdvanced ? 'rotate-180' : ''}`} aria-hidden="true" />
                  {showAdvanced ? 'Fewer options' : 'More options'}
                  {!showAdvanced && optionsCount > 0 && (
                    <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-soft px-1.5 text-[11px] font-semibold text-accent-soft-fg">
                      {optionsCount}
                    </span>
                  )}
                </button>

                {showAdvanced && (
                  <div className="flex flex-col gap-5 animate-rise-fast">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 pt-2.5">
                        <span className="block text-sm font-medium text-fg">Number of links</span>
                        <span className={`${TEXT.caption} block mt-0.5`}>Several one-time links to the same secret. Each works once.</span>
                      </div>
                      <div className="inline-flex items-center rounded-lg border border-line bg-surface overflow-hidden shrink-0">
                        <button
                          type="button"
                          aria-label="Decrease number of links"
                          onClick={() => setNumLinks(Math.max(1, numLinks - 1))}
                          disabled={numLinks <= 1}
                          className="h-11 w-10 inline-flex items-center justify-center text-fg hover:bg-fg/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <Minus className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <input
                          id="num-links"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={numLinks}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (v >= 1 && v <= 10) setNumLinks(v);
                          }}
                          aria-label="Number of links"
                          className="h-11 w-12 bg-transparent text-center text-sm font-medium tabular-nums text-fg border-x border-line focus:bg-fg/5 focus-visible:outline-offset-[-2px]"
                        />
                        <button
                          type="button"
                          aria-label="Increase number of links"
                          onClick={() => setNumLinks(Math.min(10, numLinks + 1))}
                          disabled={numLinks >= 10}
                          className="h-11 w-10 inline-flex items-center justify-center text-fg hover:bg-fg/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </div>
                    </div>

                    <ShareOptionToggle
                      id="notify-toggle"
                      checked={enableNotification}
                      onChange={() => {
                        setEnableNotification(!enableNotification);
                        if (enableNotification) {
                          setNotificationEmail('');
                          setEmailValidation({ isValid: null, message: '' });
                        }
                      }}
                      label="Email me when it's opened"
                      hint="With the time and nothing else."
                    >
                      <input
                        type="email"
                        autoComplete="email"
                        maxLength={MAX_EMAIL_LENGTH}
                        value={notificationEmail}
                        onChange={(e) => {
                          setNotificationEmail(e.target.value);
                          setEmailValidation(validateEmail(e.target.value));
                        }}
                        placeholder="you@company.com"
                        aria-label="Notification email"
                        aria-invalid={emailValidation.isValid === false}
                        className={`max-w-[340px] ${inputClass(emailValidation.isValid === false)}`}
                      />
                      {emailValidation.isValid === false && (
                        <p className="text-xs text-danger">{emailValidation.message}</p>
                      )}
                    </ShareOptionToggle>

                    <ShareOptionToggle
                      id="ip-toggle"
                      checked={enableIPRestriction}
                      onChange={() => {
                        setEnableIPRestriction(!enableIPRestriction);
                        if (enableIPRestriction) setCustomAllowedIPs('');
                      }}
                      label="Restrict by IP address"
                      hint="Only listed IPs can open the link."
                    >
                      <input
                        type="text"
                        value={customAllowedIPs}
                        onChange={(e) => setCustomAllowedIPs(e.target.value)}
                        maxLength={MAX_ALLOWED_IPS_LENGTH}
                        placeholder="203.0.113.1, 198.51.100.2"
                        aria-label="Allowed IP addresses"
                        className={`max-w-[340px] font-mono text-[13px] ${inputClass()}`}
                      />
                      <p className={TEXT.caption}>Leave blank to allow only your current IP.</p>
                    </ShareOptionToggle>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col gap-2.5 mt-1">
              <Button
                block
                size="lg"
                onClick={handleEncrypt}
                disabled={cannotCreate}
                aria-busy={isEncrypting}
              >
                {isEncrypting ? (
                  <>
                    <Spinner />
                    Encrypting in your browser...
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {createLabel}
                  </>
                )}
              </Button>
              <p className={`${TEXT.caption} text-center`}>{nextUp}</p>
            </div>
          </div>
        ) : (
          <div className={`${SURFACE_RAISED} p-5 sm:p-6 flex flex-col gap-5 animate-rise-slow`}>
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className={TAG_ACCENT}>
                {hasMultiLinks ? `${wrappedTokens.length} links created` : 'Link created'}
              </span>
              <span className={`${TEXT.caption} tabular-nums`}>
                {linkCreatedAt}
                {remainingLabel ? ` · ${remainingLabel}` : ''}
              </span>
            </div>

            <h2 className={TEXT.h2}>
              {hasMultiLinks
                ? 'Copy each link now - this is the only time you will see them'
                : 'Copy it now - this is the only time you will see it'}
            </h2>

            {hasMultiLinks ? (
              <div className="flex flex-col gap-3">
                {wrappedTokens.map((tokenInfo, index) => {
                  const fullUrl = generateSecretLink(tokenInfo.token);
                  const isLinkCopied = copiedLinks.has(index);
                  const linkNumber = tokenInfo.link_number || index + 1;
                  return (
                    <div key={linkNumber} className={`${INSET} p-3.5 sm:p-4 min-w-0`}>
                      <div className="flex items-center justify-between gap-2 mb-2.5 min-w-0">
                        <span className="text-sm font-medium text-fg truncate">Link {linkNumber}</span>
                        <button
                          type="button"
                          aria-expanded={Boolean(showQRCodes[index])}
                          onClick={() => setShowQRCodes((prev) => ({ ...prev, [index]: !prev[index] }))}
                          className="inline-flex items-center gap-1.5 h-8 px-2 -mr-2 rounded-md text-xs font-medium text-accent-text hover:bg-accent/10 transition-colors"
                        >
                          <QrCode className="h-3.5 w-3.5" aria-hidden="true" />
                          {showQRCodes[index] ? 'Hide QR' : 'QR code'}
                        </button>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <input
                          type="text"
                          value={fullUrl}
                          readOnly
                          onFocus={selectInputText}
                          aria-label={`Shareable link ${linkNumber}`}
                          className={`${MONO_FIELD} bg-surface`}
                        />
                        <Button
                          onClick={() => copyLinkAtIndex(fullUrl, index)}
                          className="min-w-[116px]"
                          variant={isLinkCopied ? 'secondary' : 'primary'}
                        >
                          {isLinkCopied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
                          {isLinkCopied ? 'Copied' : 'Copy link'}
                        </Button>
                      </div>
                      {showQRCodes[index] && (
                        <div className="mt-4">
                          <QrCard
                            value={fullUrl}
                            filename={`hushify-link-${linkNumber}.png`}
                            onError={setError}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <>
                <div className="flex gap-2 flex-wrap">
                  <input
                    type="text"
                    value={shareLink}
                    readOnly
                    onFocus={selectInputText}
                    aria-label="Your secret link"
                    className={MONO_FIELD}
                  />
                  <Button
                    onClick={() => copyLinkAtIndex(shareLink, 'single')}
                    className="min-w-[124px]"
                    variant={linkCopied ? 'secondary' : 'primary'}
                  >
                    {linkCopied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
                    {linkCopied ? 'Copied' : 'Copy link'}
                  </Button>
                </div>

                <div>
                  <button
                    type="button"
                    aria-expanded={Boolean(showQRCodes.single)}
                    onClick={() => setShowQRCodes((prev) => ({ ...prev, single: !prev.single }))}
                    className="inline-flex items-center gap-1.5 h-9 px-2 -ml-2 rounded-md text-sm font-medium text-accent-text hover:bg-accent/10 transition-colors"
                  >
                    <QrCode className="h-4 w-4" aria-hidden="true" />
                    {showQRCodes.single ? 'Hide QR code' : 'Show QR code'}
                  </button>
                  {showQRCodes.single && (
                    <div className="mt-4">
                      <QrCard value={shareLink} filename="hushify-link.png" onError={setError} />
                    </div>
                  )}
                </div>
              </>
            )}

            <ul className="flex flex-col gap-2 border-t border-line pt-5">
              {checklist.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-[13px] text-muted">
                  <Check className="h-3.5 w-3.5 mt-[3px] shrink-0 text-accent" strokeWidth={2.5} aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
              <Button variant="secondary" onClick={handleReset}>Share another secret</Button>
              <span className={TEXT.caption}>
                Leaving this page destroys the key. We can&apos;t send it to you again.
              </span>
            </div>
          </div>
        )}
      </div>

      {!showEncryptResult && (
        <section className={`${CONTENT_WIDTH} mt-8 sm:mt-10 pt-8 sm:pt-10 pb-16 sm:pb-24 border-t border-line`} aria-label="Self-hosted demo">
          <p className={TEXT.eyebrow}>Self-hosted</p>

          <DemoVideo className="mt-3" />

          {onNavigate && (
            <div className="mt-6">
              <Button variant="ghost" onClick={() => onNavigate('pricing', { tab: 'self-hosted' })}>
                See Self-Hosted pricing
              </Button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
